import React, { useState, MouseEvent } from "react";
import styles from "./Card.module.css";
import cardBack from "../../assets/cardBack.png";
import { Modal } from "react-bootstrap"; // Import Bootstrap Modal and Button
import "./modal.css";
import { CardData, getCardById } from "../../services/CardServices";
import { BoosterCards } from "../../services/BoosterServices";
import NewICon from "../CssIcon/NewIcon";
import { rarityEnum } from "../../enums/rarityEnum";

interface CardProps {
  isHiddenProp?: boolean;
  disableClick?: boolean;
  cardData: CardData | BoosterCards;
}

function throttle<T extends (...args: any[]) => any>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let lastCall = 0;
  return (...args: Parameters<T>) => {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return func(...args);
  };
}

const Cards: React.FC<CardProps> = ({
  isHiddenProp = false,
  disableClick = false,
  cardData,
}) => {
  const [isHidden, setIsHidden] = React.useState(isHiddenProp);
  const [showModal, setShowModal] = React.useState(false); // State for the modal visibility
  const [rotate, setRotate] = useState({ x: 0, y: 0 });
  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 2);

  const cardRef = React.useRef<HTMLDivElement>(null);
  const cardModalRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    setIsHidden(isHiddenProp);
  }, [isHiddenProp]);

  const handleCardClick = async () => {
    if (disableClick) return;
    const image = await getCardById(cardData.id);
    cardData.image_id = image;

    if (isHidden) {
      setIsHidden((prev) => !prev);
      const card = cardRef.current;
      if (card) {
        const delta = isHidden ? 0 : 180;
        card.style.transform = `rotateX(0deg) rotateY(${delta}deg)`;
      }
    } else {
      setShowModal(true);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const onMouseMove = throttle((e: MouseEvent<HTMLDivElement>) => {
    const card = e.currentTarget;
    const box = card.getBoundingClientRect();
    const x = e.clientX - box.left;
    const y = e.clientY - box.top;
    const centerX = box.width / 2;
    const centerY = box.height / 2;
    const rotateX = (y - centerY) / 30;
    const rotateY = (centerX - x) / 30;

    setRotate({ x: rotateX, y: rotateY });
  }, 100);

  const onMouseLeave = () => {
    setRotate({ x: 0, y: 0 });
  };

  return (
    <div className={styles.cardContainer}>
      <div
        key={cardData.id}
        ref={cardRef}
        className={`${styles.customCard} ${
          cardData.rarity === rarityEnum.gold && styles.goldGlow
        } ${cardData.rarity === rarityEnum.silver && styles.silverGlow}
        
        ${isHidden ? styles.hide : ""} ${styles.icon}`}
        onClick={handleCardClick}
        onMouseMove={onMouseMove}
        onMouseLeave={onMouseLeave}
        style={{
          transform: `perspective(1000px) rotateX(${
            showModal ? 0 : rotate.x
          }deg) rotateY(${showModal ? 0 : rotate.y}deg) scale3d(1, 1, 1)`,
          transition: "all 400ms cubic-bezier(0.03, 0.98, 0.52, 0.99) 0s",
        }}
      >
        {new Date(cardData.updated_at) > oneWeekAgo && (
          <div className={styles.newIcon}>
            <NewICon />
          </div>
        )}
        {cardData && (
          <img
            src={cardData.small_image_id}
            alt={cardData.name}
            className={`${styles.cardImage}  ${
              isHidden ? styles.hidden : styles.visible
            }`}
          />
        )}
        <img
          src={cardBack}
          alt="cardBack"
          className={`${styles.cardImage} ${
            isHidden ? styles.visible : styles.hidden
          } `}
        />
      </div>

      <Modal
        show={showModal}
        onHide={handleCloseModal}
        centered
        size="xl"
        className="d-flex my-modal"
      >
        {cardData ? (
          <div
            className={styles.cardExpandContainer}
            ref={cardModalRef}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            onClick={handleCloseModal}
            style={{
              transform: `perspective(1000px) rotateX(${rotate.x}deg) rotateY(${rotate.y}deg) scale3d(1, 1, 1)`,
              transition: "all 400ms cubic-bezier(0.03, 0.98, 0.52, 0.99) 0s",
            }}
          >
            <img
              src={cardData.image_id}
              alt={cardData.name}
              className={styles.cardImage}
            />
          </div>
        ) : (
          <p>Loading...</p>
        )}
      </Modal>
    </div>
  );
};

export default Cards;
